import { getLocale, t, tn, useI18n } from '../../i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiKey, FiSearch, FiUserPlus } from 'react-icons/fi';
import {
  isAwaitingSetup,
  hasIrishEroMinimumHoursIssue,
  missingProfileDetails,
  staffService,
  type StaffRole,
  type StaffRow,
} from '../../services/staffService';
import type { StaffAccess } from '../../services/roleService';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import AddStaffModal, { type StaffInviteOutcome } from './AddStaffModal';
import RoleChips from './RoleChips';
import { getStaffInitials } from './staffPresentation';
import './StaffPage.css';

type RoleFilter = 'all' | StaffRole;

interface Props {
  access: StaffAccess;
  /** Omitted for anyone who may not issue join codes, and the button goes with it. */
  onOpenJoinCodes?: () => void;
  onSelectStaff?: (id: string) => void;
}

/**
 * Somebody can be both a guard and a manager, so the tabs overlap: a guard
 * who also does the rosters is under Guards and under Managers.
 */
const matchesFilter = (member: StaffRow, filter: RoleFilter): boolean => {
  switch (filter) {
    case 'guard': return member.is_guard;
    case 'manager': return member.role === 'manager';
    case 'admin': return member.role === 'admin';
    default: return true;
  }
};

const ROLE_FILTERS: RoleFilter[] = ['all', 'guard', 'manager', 'admin'];

const roleFilterLabel = (filter: RoleFilter): string => {
  switch (filter) {
    case 'guard': return t('Guards');
    case 'manager': return t('Managers');
    case 'admin': return t('Admins');
    default: return t('All staff');
  }
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return t('N/A');
  return new Date(dateStr).toLocaleDateString(getLocale());
};

export default function StaffPage({ access, onOpenJoinCodes, onSelectStaff }: Props) {
  useI18n();
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ tone: 'success' | 'warning'; text: string } | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [missingOnly, setMissingOnly] = useState(false);

  const loadStaff = useCallback(async () => {
    try {
      setStaff(await staffService.fetchStaff());
      setError('');
    } catch (err) {
      setError((err as Error).message || t('Could not load your staff.'));
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    void loadStaff().finally(() => setIsLoading(false));
  }, [loadStaff]);

  const handleInvited = (result: StaffInviteOutcome) => {
    setIsAdding(false);
    setNotice(!result.emailSent
      ? {
          tone: 'warning',
          text: t('{email} was added, but the invite email could not be sent. Open their profile and choose Resend invite.', { email: result.email }),
        }
      : !result.contractHoursSaved
        ? {
            tone: 'warning',
            text: t('Invite sent to {email}, but their contract hours could not be saved. Set them from their profile.', { email: result.email }),
          }
        : {
            tone: 'success',
            text: t('Invite sent to {email}. They are on your staff list now and can be rostered straight away.', { email: result.email }),
          });
    void loadStaff();
  };

  const disabledCount = staff.filter((member) => member.status === 'disabled').length;

  // Deactivated people stay out of the counts unless they are being shown, so
  // a tab never promises more cards than it opens onto.
  const inScope = useMemo(
    () => (showDisabled ? staff : staff.filter((member) => member.status !== 'disabled')),
    [staff, showDisabled],
  );

  const countFor = (filter: RoleFilter): number => (
    inScope.filter((member) => matchesFilter(member, filter)).length
  );

  const missingDetailsCount = staff.filter((member) => missingProfileDetails(member).length > 0).length;
  const eroHoursIssueCount = staff.filter((member) => hasIrishEroMinimumHoursIssue(member)).length;
  const showingMissingOnly = missingOnly && missingDetailsCount > 0;

  const visibleStaff = useMemo(() => {
    const term = search.trim().toLowerCase();
    return inScope.filter((member) => {
      if (showingMissingOnly && missingProfileDetails(member).length === 0) return false;
      if (!matchesFilter(member, roleFilter)) return false;
      if (!term) return true;
      return (member.full_name ?? '').toLowerCase().includes(term)
        || (member.email ?? '').toLowerCase().includes(term);
    });
  }, [inScope, roleFilter, search, showingMissingOnly]);

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">{t('Staff')}</h1>
          <p className="page__subtitle">
            {t('Everyone in your company: admins, managers and guards.')}
          </p>
        </div>
        <div className="page__header-actions">
          {disabledCount > 0 && (
            <button
              className="btn btn--secondary"
              type="button"
              onClick={() => setShowDisabled((current) => !current)}
            >
              {showDisabled
                ? t('Hide deactivated')
                : t('Show {count} deactivated', { count: disabledCount })}
            </button>
          )}
          {onOpenJoinCodes && (
            <button className="btn btn--secondary staff-page__action" type="button" onClick={onOpenJoinCodes}>
              <FiKey aria-hidden="true" />
              {t('Join Codes')}
            </button>
          )}
          <button className="btn btn--primary staff-page__action" type="button" onClick={() => setIsAdding(true)}>
            <FiUserPlus aria-hidden="true" />
            {t('Add user')}
          </button>
        </div>
      </div>

      <div aria-live="polite">
        {notice && (
          <div className={notice.tone === 'success' ? 'msg--success' : 'staff-page__warning'}>
            {notice.text}
          </div>
        )}
      </div>
      {error && <div className="staff-page__error" role="alert">{error}</div>}

      {missingDetailsCount > 0 && (
        <div className="staff-page__missing">
          <FiAlertCircle aria-hidden="true" />
          <span className="staff-page__missing-text">
            {tn(
              '{count} guard has no contract hours on file. Auto scheduling cannot give them their hours until you set them.',
              '{count} guards have no contract hours on file. Auto scheduling cannot give them their hours until you set them.',
              missingDetailsCount,
            )}
          </span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            aria-pressed={showingMissingOnly}
            onClick={() => setMissingOnly((current) => !current)}
          >
            {showingMissingOnly ? t('Show everyone') : t('Show them')}
          </button>
        </div>
      )}

      {eroHoursIssueCount > 0 && (
        <div className="staff-page__missing" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span className="staff-page__missing-text">
            {tn(
              '{count} Irish guard has completed six months of service without a contract guaranteeing at least 24 hours per week.',
              '{count} Irish guards have completed six months of service without contracts guaranteeing at least 24 hours per week.',
              eroHoursIssueCount,
            )}
          </span>
        </div>
      )}

      <div className="staff-page__toolbar">
        <div className="tabs staff-page__tabs" role="group" aria-label={t('Filter staff by role')}>
          {ROLE_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              aria-pressed={roleFilter === filter}
              className={`tab-btn${roleFilter === filter ? ' tab-btn--active' : ''}`}
              onClick={() => setRoleFilter(filter)}
            >
              {roleFilterLabel(filter)}
              <span className="staff-page__tab-count">{countFor(filter)}</span>
            </button>
          ))}
        </div>
        <label className="staff-page__search">
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            className="form__input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Search by name or email')}
            aria-label={t('Search by name or email')}
          />
        </label>
      </div>

      {staff.length === 0 && !error ? (
        <p className="text-muted">
          {t('No staff yet. Add a user to email them an invite, or share a join code with guards who already have the app.')}
        </p>
      ) : visibleStaff.length === 0 ? (
        <p className="text-muted">{t('Nobody matches these filters.')}</p>
      ) : (
        <div className="grid-3">
          {visibleStaff.map((member) => {
            const avatarUrl = staffService.getAvatarUrl(member.avatar_url);
            const displayName = member.full_name || member.email || t('Unknown');
            const missingContractHours = missingProfileDetails(member).includes('contract_hours');
            const eroHoursIssue = hasIrishEroMinimumHoursIssue(member);
            return (
              <button
                key={member.id}
                type="button"
                className={`card--clickable staff-card${member.status === 'disabled' ? ' card--muted' : ''}`}
                onClick={() => onSelectStaff?.(member.id)}
              >
                <span className="staff-card__top">
                  <span
                    className="avatar staff-card__avatar"
                    style={avatarUrl ? undefined : getAvatarAccentStyle(member.id || member.full_name)}
                  >
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="" className="avatar__img" />
                    ) : (
                      getStaffInitials(member.full_name, member.email)
                    )}
                  </span>
                  <RoleChips roles={member.roles} tier={member.role} isOwner={member.is_owner} />
                </span>
                <span className="staff-card__name">{displayName}</span>
                <span className="staff-card__detail" title={member.email ?? undefined}>
                  {member.email || t('No email')}
                </span>
                {member.phone && <span className="staff-card__detail">{member.phone}</span>}
                {missingContractHours && (
                  <span className="staff-card__missing">
                    <FiAlertCircle aria-hidden="true" />
                    {t('No contract hours')}
                  </span>
                )}
                {eroHoursIssue && (
                  <span className="staff-card__missing">
                    <FiAlertCircle aria-hidden="true" />
                    {t('Irish ERO: 24-hour contract required')}
                  </span>
                )}
                <span className="staff-card__footer">
                  {member.status === 'disabled' ? (
                    <span className="status-chip status-chip--neutral">{t('Deactivated')}</span>
                  ) : member.invite_status === 'expired' ? (
                    <span className="status-chip status-chip--warning">{t('Invite expired')}</span>
                  ) : isAwaitingSetup(member) ? (
                    <span className="status-chip status-chip--info">{t('Invite sent')}</span>
                  ) : (
                    <span className="staff-card__date">{t('Joined')}: {formatDate(member.created_at)}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isAdding && (
        <AddStaffModal
          access={access}
          onInvited={handleInvited}
          onClose={() => setIsAdding(false)}
        />
      )}
    </div>
  );
}
